import { websiteBuilderNormalizeValue } from "../../schema/normalization/websiteBuilderNormalizeValue";
import { IWebsiteBuilderNodeDesignStyles } from "../websiteBuilderNode";
import {
  websiteBuilderGenerateDefaultHideableNodeVisibility,
} from "../websiteBuilderHideableNode";
import {
  IWebsiteBuilderElementBase,
  IWebsiteBuilderElementDataBase,
  TWebsiteBuilderElement,
} from "./websiteBuilderElement";

export type TWebsiteBuilderEmbedProvider = "none" | "google-calendar" | "google-maps" | "youtube";

function isWebsiteBuilderEmbedProvider(value: string): value is TWebsiteBuilderEmbedProvider {
  return value === "none" || value === "google-calendar" || value === "google-maps" || value === "youtube";
}

export interface IWebsiteBuilderEmbedSettings {
  provider: TWebsiteBuilderEmbedProvider;
  code: string;
}

export interface IWebsiteBuilderEmbedData extends IWebsiteBuilderElementDataBase {
  type: "embed";
  settings: IWebsiteBuilderEmbedSettings;
}

export interface IWebsiteBuilderEmbed extends IWebsiteBuilderElementBase {
  element: IWebsiteBuilderEmbedData;
}

export function isWebsiteBuilderEmbed(node: TWebsiteBuilderElement): node is IWebsiteBuilderEmbed {
  return node?.element?.type === "embed";
}

function generateDefaultEmbedSettings(): IWebsiteBuilderEmbedSettings {
  return {
    provider: "none",
    code: "",
  };
}

export function websiteBuilderGenerateDefaultEmbed(): IWebsiteBuilderEmbed {
  return {
    id: crypto.randomUUID(),
    type: "element",
    element: {
      type: "embed",
      layout: {
        desktop: {
          position: { x: 0, y: 0 },
          dimensions: { width: 400, height: 225 }, // 16:9
        },
        tablet: {
          position: { x: 0, y: 0 },
          dimensions: { width: 320, height: 180 }, // 16:9
        },
        mobile: {
          position: { x: 0, y: 0 },
          dimensions: { width: 288, height: 162 }, // 16:9
        },
      },
      visibility: websiteBuilderGenerateDefaultHideableNodeVisibility(),
      zIndex: 0,
      settings: generateDefaultEmbedSettings(),
    },
  };
}

export function websiteBuilderNormalizeEmbedSettings(
  partialSettings: Partial<IWebsiteBuilderEmbedSettings>,
): IWebsiteBuilderEmbedSettings {
  const defaultSettings = generateDefaultEmbedSettings();

  if (partialSettings == null) {
    return defaultSettings;
  }

  return {
    provider: websiteBuilderNormalizeValue({
      type: "string",
      value: partialSettings.provider,
      defaultValue: defaultSettings.provider,
      required: true,
      validate: isWebsiteBuilderEmbedProvider,
    }) as TWebsiteBuilderEmbedProvider,
    code: websiteBuilderNormalizeValue({
      type: "string",
      value: partialSettings.code,
      defaultValue: defaultSettings.code,
      required: true,
    }),
  };
}

interface IWebsiteBuilderEmbedDesignStyles extends IWebsiteBuilderNodeDesignStyles {
  containerClassName: string;
}

export function websiteBuilderGenerateEmbedDesignStyles(): IWebsiteBuilderEmbedDesignStyles {
  return {
    containerClassName: "website-builder-embed-container",
    className: "website-builder-embed",
    styleProperties: {},
    styleDeclaration: "",
  };
}

interface IWebsiteBuilderEmbedIframeSettings {
  src: string;
  loading: "lazy";
  title: string;
  referrerPolicy: ReferrerPolicy;
  allowFullScreen?: boolean;
  allow?: string;
}

export function websiteBuilderGenerateEmbedIframeSettings(
  settings: IWebsiteBuilderEmbedSettings,
): IWebsiteBuilderEmbedIframeSettings {
  const src = settings.code?.match(/src=["']([^"']+)["']/)?.[1];

  if (src == null) {
    return null;
  }

  const sharedSettings = {
    src,
    loading: "lazy" as const,
  };

  switch (settings.provider) {
    case "google-calendar":
      if (!src.startsWith("https://calendar.google.com/calendar/embed")) {
        return null;
      }

      return {
        ...sharedSettings,
        title: "Google calendar", // TODO: Translation
        referrerPolicy: "strict-origin-when-cross-origin",
      };
    case "google-maps":
      if (!src.startsWith("https://www.google.com/maps/embed")) {
        return null;
      }

      return {
        ...sharedSettings,
        title: "Google map", // TODO: Translation
        referrerPolicy: "no-referrer-when-downgrade",
        allowFullScreen: true,
      };
    case "youtube":
      if (!src.startsWith("https://www.youtube.com/embed/") && !src.startsWith("https://www.youtube-nocookie.com/embed/")) {
        return null;
      }

      return {
        ...sharedSettings,
        title: "YouTube video player", // TODO: Translation
        referrerPolicy: "strict-origin-when-cross-origin",
        allowFullScreen: true,
        allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share",
      };
    default:
      return null;
  }
}
